import { trackBannerRequestOfferClick } from "~/app/analytics/ga/affiliate";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { Box, Button, styled, Typography } from "~/common/mui";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";

interface Props {
  className?: string;
  sx?: import("~/common/mui").SxProps;
}

export interface AffiliateRequestOfferBannerViewProps extends Props {
  text: string;
  buttonLabel: string;
  isTablet: boolean;
  onRequestOffer: () => void;
}

const RequestOfferContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.surface.main,
  borderRadius: theme.spacing(0.5),
  [theme.breakpoints.up("desktop-s")]: {
    padding: `${theme.spacing(2.5)} ${theme.spacing(3.75)}`,
  },
}));

const RequestOfferText = styled(Typography)(({ theme }) => ({
  flex: 2,
  margin: `0 ${theme.spacing(1.25)}`,
}));

export const AffiliateRequestOfferBannerView = ({
  className,
  sx,
  text,
  buttonLabel,
  isTablet,
  onRequestOffer,
}: AffiliateRequestOfferBannerViewProps) => {
  return (
    <RequestOfferContainer className={className} sx={sx}>
      <RequestOfferText>{text}</RequestOfferText>
      <Button
        sx={{ width: "100%", minHeight: 30, mt: isTablet ? 0 : 3, ...(isTablet && { width: "auto", mx: 2.5 }) }}
        onClick={onRequestOffer}
        data-test-id={"banner-partner-request-offer"}
      >
        {buttonLabel}
      </Button>
    </RequestOfferContainer>
  );
};

export const AffiliateRequestOfferBanner = ({ className, sx }: Props) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();

  return (
    <AffiliateRequestOfferBannerView
      className={className}
      sx={sx}
      text={
        userHasPermission(Permission.ADD_TO_CART)
          ? t("affiliateSalesPartner.banner.new.text.wishlist")
          : t("affiliateSalesPartner.banner.text.wishlist")
      }
      buttonLabel={t("affiliateSalesPartner.banner.button.requestOffer")}
      isTablet={isTablet}
      onRequestOffer={() => {
        trackBannerRequestOfferClick();
        redirectToUrl(CART_PAGE);
      }}
    />
  );
};
